import { __read } from 'tslib';
import { jsx } from 'react/jsx-runtime';
import { createContext, useMemo, useState, useCallback } from 'react';
import { useAnalytics } from '../../analytics/hooks.js';
import '../../analytics/context.js';
import { FORM_FIELDS } from '../../constants/form.js';
import '../../constants/sources.js';
import { perMonthPayment } from '../../helpers/calculating.js';
import { periodToNumber } from '../../helpers/fomatters.js';
import { AMOUNT_LIMITS, PERIOD_OPTIONS, PURPOSE_OPTIONS, PERIOD_SOURCE_VALUES, PURPOSE_SOURCE_VALUES } from '../ConditionsSelector/constants.js';
import { defaultCalculatorEventHandler } from './event.handler.js';

var CalculatorContext = createContext(null);
var CalculatorActionsContext = createContext(null);
var CalculatorProvider = function (props) {
    var _a, _b;
    var children = props.children, defaultFormValues = props.defaultFormValues, baseUrl = props.baseUrl, customFormParams = props.formParams, _c = props.onFieldChange, onFieldChange = _c === void 0 ? function () { } : _c, onEvent = props.onEvent;
    var pushEvent = useAnalytics().pushEvent;
    var _d = customFormParams || {}, amountParams = _d.amount, customPeriodOptions = _d.periodOptions, customPurposeOptions = _d.purposeOptions;
    var _e = amountParams || {}, customStep = _e.step, customMaxAmount = _e.max, customMinAmount = _e.min;
    var eventHadler = useMemo(function () {
        if (onEvent) {
            return onEvent;
        }
        return defaultCalculatorEventHandler(pushEvent);
    }, [onEvent, pushEvent]);
    var maxAmount = useMemo(function () { return (customMaxAmount ? Math.max(customMaxAmount, AMOUNT_LIMITS.MIN) : AMOUNT_LIMITS.MAX); }, [customMaxAmount]);
    var amountStep = useMemo(function () { return (customStep ? Math.max(Math.min(maxAmount / 2, customStep), 1) : AMOUNT_LIMITS.STEP); }, [customStep, maxAmount]);
    var minAmount = useMemo(function () { return (customMinAmount ? Math.min(Math.max(customMinAmount, 0), maxAmount - 1) : AMOUNT_LIMITS.MIN); }, [customMinAmount, maxAmount]);
    var periodOptions = useMemo(function () { return (customPeriodOptions && (customPeriodOptions === null || customPeriodOptions === void 0 ? void 0 : customPeriodOptions.length) > 0 ? customPeriodOptions : PERIOD_OPTIONS); }, [customPeriodOptions]);
    var purposeOptions = useMemo(function () { return (customPurposeOptions && (customPurposeOptions === null || customPurposeOptions === void 0 ? void 0 : customPurposeOptions.length) > 0 ? customPurposeOptions : PURPOSE_OPTIONS); }, [customPurposeOptions]);
    var _f = defaultFormValues || {}, _g = _f.amount, defaultAmount = _g === void 0 ? 100000 : _g, _h = _f.period, defaultPeriod = _h === void 0 ? PERIOD_SOURCE_VALUES.P5Y : _h, _j = _f.purpose, defaultPurpose = _j === void 0 ? PURPOSE_SOURCE_VALUES.MONEY : _j, _k = _f.rate, defaultRate = _k === void 0 ? 31 : _k;
    var _l = __read(useState(Math.max(Math.min(defaultAmount, maxAmount), minAmount)), 2), amount = _l[0], setAmount = _l[1];
    var _m = __read(useState(defaultRate), 2), rate = _m[0], setRate = _m[1];
    var _o = __read(useState(((_a = periodOptions.find(function (item) { return item.value === defaultPeriod; })) === null || _a === void 0 ? void 0 : _a.value) ||
        (periodOptions.includes(PERIOD_SOURCE_VALUES.P5Y) ? PERIOD_SOURCE_VALUES.P5Y : periodOptions[0].value)), 2), period = _o[0], setPeriod = _o[1];
    var _p = __read(useState(((_b = purposeOptions.find(function (item) { return item.value === defaultPurpose; })) === null || _b === void 0 ? void 0 : _b.value) ||
        (purposeOptions.includes(PURPOSE_SOURCE_VALUES.MONEY)
            ? PURPOSE_SOURCE_VALUES.MONEY
            : purposeOptions[0].value)), 2), purpose = _p[0], setPurpose = _p[1];
    var purposeLabel = useMemo(function () { var _a; return (_a = purposeOptions.find(function (item) { return item.value === purpose; })) === null || _a === void 0 ? void 0 : _a.label; }, [purpose, purposeOptions]);
    var periodLabel = useMemo(function () { var _a; return (_a = periodOptions.find(function (item) { return item.value === period; })) === null || _a === void 0 ? void 0 : _a.label; }, [period, periodOptions]);
    var payment = useMemo(function () {
        var term = periodToNumber(period);
        return perMonthPayment(amount, term, rate);
    }, [amount, period, rate]);
    var eventFormValues = useMemo(function () { return ({
        amount: amount,
        period: {
            value: period,
            label: periodLabel,
        },
        purpose: {
            value: purpose,
            label: purposeLabel,
        },
        rate: rate,
        payment: payment,
    }); }, [amount, period, purpose, rate, payment, periodLabel, purposeLabel]);
    var formValues = useMemo(function () {
        var _a;
        return (_a = {},
            _a[FORM_FIELDS.AMOUNT] = amount,
            _a[FORM_FIELDS.PERIOD] = period,
            _a[FORM_FIELDS.PURPOSE] = purpose,
            _a.rate = rate,
            _a.payment = payment,
            _a);
    }, [amount, period, purpose, rate, payment]);
    var formParams = useMemo(function () { return ({
        amount: {
            step: amountStep,
            max: maxAmount,
            min: minAmount,
        },
        periodOptions: periodOptions,
        purposeOptions: purposeOptions,
    }); }, [amountStep, maxAmount, minAmount, periodOptions, purposeOptions]);
    var value = useMemo(function () { return ({
        baseUrl: baseUrl,
        formValues: formValues,
        eventFormValues: eventFormValues,
        formParams: formParams,
        onEvent: eventHadler,
    }); }, 
    // ignore analyticsEvents object
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseUrl, formValues, eventFormValues, formParams]);
    var updateFormData = useCallback(function (fieldName, fieldValue) {
        var _a;
        var updateFieldMap = (_a = {},
            _a[FORM_FIELDS.AMOUNT] = setAmount,
            _a[FORM_FIELDS.PERIOD] = setPeriod,
            _a[FORM_FIELDS.PURPOSE] = setPurpose,
            _a);
        if (!Object.keys(updateFieldMap).includes(fieldName)) {
            return;
        }
        updateFieldMap[fieldName](fieldValue);
        onFieldChange(fieldName, fieldValue);
    }, [setAmount, setPeriod, setPurpose, onFieldChange]);
    var actions = useMemo(function () { return ({ updateFormData: updateFormData, setRate: setRate }); }, [updateFormData, setRate]);
    return (jsx(CalculatorContext.Provider, { value: value, children: jsx(CalculatorActionsContext.Provider, { value: actions, children: children }) }));
};

export { CalculatorActionsContext, CalculatorContext, CalculatorProvider };
//# sourceMappingURL=context.js.map
